// apps/merchant-v3/src/lib/theme/ThemeProvider.tsx
//
// Phase 6 Wave 2 Task 06 — Theme provider. Reads the persisted Zustand
// `useThemeStore` preference and applies the resolved theme as the
// `dark` class on `<html>` (Tailwind 4 `darkMode: 'class'` per
// `tailwind.config.ts`). Listens for system preference changes via
// `matchMedia('(prefers-color-scheme: dark)')` so 'system' mode follows
// the OS toggle in real time.
//
// CSS variables for theme tokens (--background, --foreground, --border,
// --ring) live in `index.css` and are switched automatically by the
// `.dark` class.
//
// vg-binding: PLAN:task-06
// vg-binding: P6.D-221

import { useEffect, type ReactNode } from 'react';

import { useThemeStore, type ThemePreference } from './themeStore';

export interface ThemeProviderProps {
  children: ReactNode;
}

/** Resolve a preference value to the concrete `light`|`dark` to apply.
 *
 * 'system' currently pins to 'light' regardless of OS. Reason: merchant-v3
 * components were authored without dark:* Tailwind variants, so following
 * OS dark caused white text on light card bg (invisible). Phase 6 design
 * is light-mode-first (DESIGN.md §1); full dark pass is deferred. The
 * themeStore still persists the preference so a toggle UI can ship later
 * without a data migration.
 */
export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'dark') return 'dark';
  if (pref === 'light') return 'light';
  // 'system' (Auto / Match system) — follow the OS prefers-color-scheme so a
  // Mac/Windows on "auto" appearance flips the app to dark at night too.
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'light';
}

function applyThemeToDom(resolved: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  // Do NOT set root.style.colorScheme — the inline style overrides the
  // `:root { color-scheme: light }` pin in index.css (debug B6) and lets
  // the browser repaint native form controls using a dark UA palette,
  // producing white text on white card bg.
}

export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const pref = useThemeStore((s) => s.theme);

  useEffect(() => {
    applyThemeToDom(resolveTheme(pref));
  }, [pref]);

  // Track system preference changes only when in 'system' mode.
  useEffect(() => {
    if (pref !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => applyThemeToDom(resolveTheme('system'));
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [pref]);

  return <>{children}</>;
}

export default ThemeProvider;
